/** Variable substitution + manifest dialog / investigation conditions (Section 13.3) */
import type {
  CaseManifestV1,
  DialogConditionV1,
} from "@cold-case/shared/case-manifest";

export function substituteDialog(
  text: string | null | undefined,
  names: string[],
  victimName: string | null | undefined
): string {
  const safe = String(text ?? "").trim();
  if (!safe) return "";
  const victim = String(victimName ?? "the victim").trim() || "the victim";
  return safe
    .replace(/\{\{player1_name\}\}/g, names[0] ?? "Detective")
    .replace(/\{\{player2_name\}\}/g, names[1] ?? "")
    .replace(/\{\{player3_name\}\}/g, names[2] ?? "")
    .replace(/\{\{victim_name\}\}/g, victim);
}

/** Journal fields used for manifest condition evaluation (client + seeds). */
export type CoherenceJournal = {
  discovered_clues?: readonly string[] | string[];
  talked_to_npcs?: readonly string[] | string[];
  visited_buildings?: readonly string[] | string[];
  /** userId → NPC instance_ids that granted building search clearance (dialog progress). */
  building_clearance_by_user?: Readonly<Record<string, readonly string[] | string[]>>;
};

function clueSet(j: CoherenceJournal): Set<string> {
  return new Set([...(j.discovered_clues ?? [])].map(String));
}

function talkedSet(j: CoherenceJournal): Set<string> {
  return new Set([...(j.talked_to_npcs ?? [])].map(String));
}

function visitedSet(j: CoherenceJournal): Set<string> {
  return new Set([...(j.visited_buildings ?? [])].map(String));
}

export function simpleInvestigationConditionMet(
  cond: { type: "has_clue" | "talked_to" | "visited_building"; target: string },
  journal: CoherenceJournal
): boolean {
  const t = String(cond.target ?? "");
  switch (cond.type) {
    case "has_clue":
      return clueSet(journal).has(t);
    case "talked_to":
      return talkedSet(journal).has(t);
    case "visited_building":
      return visitedSet(journal).has(t);
    default:
      return false;
  }
}

/**
 * Evaluates `DialogConditionV1` against current journal + lobby size.
 * `all_players_present` is optimistic until presence is tracked in journal.
 */
export function dialogConditionMet(
  c: DialogConditionV1,
  journal: CoherenceJournal,
  playerCount: number
): boolean {
  const t = String(c.target ?? "");
  switch (c.type) {
    case "has_clue":
      return clueSet(journal).has(t);
    case "talked_to":
      return talkedSet(journal).has(t);
    case "visited_building":
      return visitedSet(journal).has(t);
    case "player_count_gte": {
      const n = Number(t);
      if (!Number.isFinite(n)) return false;
      return playerCount >= n;
    }
    case "all_players_present":
      return true;
    default:
      return false;
  }
}

export type SessionLite = {
  players: { display_name: string }[];
  caseManifest: CaseManifestV1;
};
